// Lightweight funnel telemetry. Writes to public.funnel_events.
// Per-session dedupe via sessionStorage so noisy events
// (landing_view, welcome_view) only fire once per tab session.
import { supabase } from '@/integrations/supabase/client';

export type FunnelEvent =
  | 'landing_view'
  | 'landing_cta_click'
  | 'signup_started'
  | 'signup_completed'
  | 'welcome_view'
  | 'checklist_step_completed'
  | 'checklist_completed'
  | 'intro_slideshow_dismissed';

const SESSION_ID_KEY = 'workitect-funnel-session';
const DEDUPE_PREFIX = 'workitect-funnel-seen:';

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = (crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2));
      sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return 'no-session';
  }
}

function alreadySeen(key: string): boolean {
  try {
    if (sessionStorage.getItem(DEDUPE_PREFIX + key)) return true;
    sessionStorage.setItem(DEDUPE_PREFIX + key, '1');
    return false;
  } catch {
    return false;
  }
}

export async function logFunnelEvent(
  event: FunnelEvent,
  details: Record<string, unknown> = {},
  opts: { dedupePerSession?: boolean; dedupeKey?: string } = {}
): Promise<void> {
  try {
    if (opts.dedupePerSession) {
      const key = opts.dedupeKey ?? event;
      if (alreadySeen(key)) return;
    }
    const session_id = getSessionId();
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('funnel_events' as any).insert({
      event,
      session_id,
      user_id: user?.id ?? null,
      details,
    });
  } catch {
    // Telemetry must never break the app.
  }
}
